import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  HStack,
  Icon,
  Spinner,
  useToast,
  VStack,
  SimpleGrid,
  useColorModeValue,
  Link,
  Highlight,
  Divider,
  Text,
  Heading,
  Checkbox,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { LuBuilding2, LuMail, LuSparkles } from 'react-icons/lu'
import { RegistrationSchema } from '../../../utils/schema'
import ControlInputWithHelp from '../components/ControlInputWithHelp'
import PhoneInput from '../components/PhoneInput'
import ControlPasswordWithValidation from '../components/ControlPasswordWithValidation'
import ConfirmButton from '../components/ConfirmButton'
import useAuthAPI from '../../../api/api_hooks/auth/useAuthAPI'
import { saveTokens } from '../../../api/axios/interceptor'
import { Navigate } from 'react-router-dom'
import { useGetPrams } from '../../../utils/getPrams'
import { useGetSpecialties } from '../../../api/api_hooks/specialties'
import GenericDropdown from '../../_shared/GenericDropdown'
import { useTranslation } from 'react-i18next'
import { useTranslatedQueryHook } from '../../../utils/useTranslatedQueryHook'
import OrganizationSearch from '../components/OrganizationSearch'
import {
  VerifyInvitationResponse,
  verifyInvitationToken,
} from '../../../api/api_hooks/admin/invitations'
import { planLabel } from '../../../config/plans'
import { recordLegalAcceptanceLocal } from '../../../config/legal'

type Inputs = {
  companyName: string
  organizationId?: string | null
  firstName: string
  lastName: string
  phoneNumber: string
  specialtyId: number
  email: string
  password: string
  confirmPassword: string
}

const useGetSpecialtiesTranslated = () => {
  return useTranslatedQueryHook(useGetSpecialties())
}

export default function RegisterForm() {
  const { useRegistration } = useAuthAPI()
  const token = useGetPrams('invitationToken')
  const post = useRegistration(token ? token : '')
  const toast = useToast()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<Inputs>({
    resolver: yupResolver(RegistrationSchema),
  })

  // ── CHU admin invitation flow ─────────────────────────────────────────
  // If the URL carries a token issued by the admin Invitations panel, we
  // verify it server-side, pre-fill + lock the email and the organization,
  // and show a banner explaining what's been pre-configured. A bad/expired
  // token falls back to a normal sign-up form with a red warning banner.
  const [invite, setInvite] = useState<VerifyInvitationResponse | null>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [verifying, setVerifying] = useState<boolean>(!!token)
  // An invitation already consumed (account created) → the recipient just needs
  // to log in, not see the sign-up form again.
  const [redirectToLogin, setRedirectToLogin] = useState(false)

  // Regulatory consent — the user must accept the legal documents before an
  // account can be created. Captured here (primary consent point); persisted
  // server-side on first authenticated load by LegalConsentNotice.
  const [acceptedLegal, setAcceptedLegal] = useState(false)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    setVerifying(true)
    verifyInvitationToken(token)
      .then((data) => {
        if (cancelled) return
        setInvite(data)
        setValue('email', data.email)
        if (data.organization) {
          setValue('organizationId', data.organization.id)
          setValue('companyName', data.organization.officialName)
        }
      })
      .catch((e) => {
        if (cancelled) return
        const status = e?.response?.status
        const key = e?.response?.data?.key
        if (status === 410 && key === 'error.invitation.accepted') {
          // The invitation was already used to create an account → go to login.
          setRedirectToLogin(true)
          return
        }
        if (status === 410 && key) {
          setInviteError(t(`auth.invite.${key.replace('error.invitation.', '')}` , {
            defaultValue: t('auth.invite.invalid'),
          }))
        } else if (status === 404) {
          setInviteError(t('auth.invite.notFound', { defaultValue: 'Cette invitation n\'existe pas ou a expiré.' }))
        } else {
          setInviteError(t('auth.invite.invalid', { defaultValue: 'Cette invitation est invalide.' }))
        }
      })
      .finally(() => { if (!cancelled) setVerifying(false) })
    return () => { cancelled = true }
  }, [token, setValue, t])

  const isInviteLocked = !!invite

  const onSubmit: SubmitHandler<Inputs> = ({ firstName, lastName, organizationId, ...rest }) => {
    if (!acceptedLegal) return
    // Record acceptance locally so the post-login consent modal doesn't re-prompt
    // a user who just accepted at sign-up; it is then persisted server-side.
    recordLegalAcceptanceLocal()
    const payload: Record<string, unknown> = {
      ...rest,
      contactName: `${firstName.trim()} ${lastName.trim()}`,
    }
    if (organizationId) {
      payload.organizationId = organizationId
    }
    post.mutateAsync(payload as unknown as void).catch((e) =>
      toast({
        title: e.response.data.message,
        position: 'top',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    )
  }

  const formBg = useColorModeValue('white', 'gray.800')
  const formBorder = useColorModeValue('gray.200', 'gray.700')
  const titleColor = useColorModeValue('gray.800', 'gray.100')
  const subTitleColor = useColorModeValue('gray.600', 'gray.400')
  const linkHoverBg = useColorModeValue('purple.50', 'purple.900')

  // Routing after a successful POST:
  //   - Invited user → BE returns 200 + auth tokens (already verified). Save
  //     tokens and drop them straight into the app.
  //   - Standard signup → BE returns 201 + `{ message: 'Success' }`. Send
  //     them to /check-email so they confirm via the verify email link.
  if (post.isSuccess) {
    const responseBody = post.data?.data
    const hasTokens = !!responseBody?.accessToken && !!responseBody?.refreshToken
    if (hasTokens) {
      saveTokens(responseBody)
      // Same as useLogin: without this, the sidebar's modules query (and
      // every other query keyed on the now-stale pre-auth userId) keeps
      // serving its cached result after this client-side navigate — a
      // collaborateur who just signed up sees the shared case but no
      // modules in the sidebar until a manual page refresh re-fetches
      // everything fresh.
      queryClient.invalidateQueries()
      return <Navigate to="/" replace />
    }
    return <Navigate to="/check-email" replace />
  }

  const isFormFilled: boolean = !!Object.keys(errors).length

  // Email validation pattern
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  // Invitation already consumed → account exists, show the login page instead.
  if (redirectToLogin) return <Navigate to="/login" replace />

  return (
    <Box
      w="100%"
      maxW={520}
      mx="auto"
      py={{ base: 4, md: 6 }}
    >
      <VStack spacing={6} w="100%">
        {/* Header */}
        <VStack spacing={3} w="100%" textAlign="center">
          <Heading
            as="h1"
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight="700"
            color={titleColor}
            letterSpacing="tight"
          >
            {t('auth.welcomeCareflai')}
          </Heading>
          <Text
            fontSize={{ base: 'sm', md: 'md' }}
            color={subTitleColor}
            maxW="400px"
            lineHeight="1.6"
          >
            {t('auth.createAccount')}
          </Text>
        </VStack>

        {/* ── Invitation banner ────────────────────────────────────────────
            Renders only when a token is present in the URL. Three states:
            – verifying  → spinner
            – verified   → green badge with org + plan, email locked below
            – error      → red explanatory banner, form still usable as a
                           plain sign-up so the user can still register. */}
        {verifying && (
          <Alert status="info" borderRadius="xl" variant="left-accent">
            <Spinner size="sm" mr={3} />
            <AlertDescription fontSize="sm">{t('auth.invite.verifying', { defaultValue: 'Vérification de votre invitation…' })}</AlertDescription>
          </Alert>
        )}
        {!verifying && invite && (
          <Box
            w="100%"
            bgGradient="linear(135deg, teal.50, cyan.50)"
            border="1px solid"
            borderColor="teal.200"
            borderRadius="2xl"
            p={4}
            boxShadow="md"
          >
            <HStack spacing={3} align="start">
              <Box
                w="40px" h="40px" minW="40px"
                borderRadius="xl"
                bgGradient="linear(135deg, teal.500, cyan.500)"
                color="white"
                display="grid"
                sx={{ placeItems: 'center' }}
              >
                <Icon as={LuSparkles} boxSize={5} />
              </Box>
              <VStack align="start" spacing={1} flex={1}>
                <Text fontSize="xs" fontWeight="700" color="teal.700" letterSpacing="wide" textTransform="uppercase">
                  {t('auth.invite.badgeEyebrow', { defaultValue: 'Invitation vérifiée' })}
                </Text>
                <Text fontSize="sm" color="gray.800" fontWeight="600" lineHeight="1.4">
                  {invite.organization
                    ? t('auth.invite.invitedToOrg', { org: invite.organization.officialName, defaultValue: `Vous êtes invité·e à rejoindre ${invite.organization.officialName}.` })
                    : t('auth.invite.invitedShort', { defaultValue: 'Votre invitation est vérifiée.' })}
                </Text>
                <HStack spacing={3} pt={1} flexWrap="wrap">
                  <HStack spacing={1.5}>
                    <Icon as={LuMail} color="teal.600" boxSize={3.5} />
                    <Text fontSize="xs" color="gray.700">{invite.email}</Text>
                  </HStack>
                  {invite.organization && (
                    <HStack spacing={1.5}>
                      <Icon as={LuBuilding2} color="teal.600" boxSize={3.5} />
                      <Text fontSize="xs" color="gray.700">{invite.organization.officialName}</Text>
                    </HStack>
                  )}
                  <Text fontSize="xs" color="gray.500" px={2} py={0.5} bg="white" borderRadius="full" border="1px solid" borderColor="teal.100">
                    {t('auth.invite.plan', { defaultValue: 'Formule' })}: <Text as="span" fontWeight="700" color="teal.700">{planLabel(invite.planTier)}</Text>
                  </Text>
                </HStack>
                {invite.message && (
                  <Box mt={2} px={3} py={2} bg="white" borderRadius="md" borderLeft="3px solid" borderLeftColor="teal.400" w="100%">
                    <Text fontSize="xs" color="gray.600" fontStyle="italic">« {invite.message} »</Text>
                  </Box>
                )}
              </VStack>
            </HStack>
          </Box>
        )}
        {!verifying && inviteError && (
          <Alert status="warning" borderRadius="xl" variant="left-accent">
            <AlertIcon />
            <AlertDescription fontSize="sm">{inviteError}</AlertDescription>
          </Alert>
        )}

        {/* Form Card */}
        <Box
          w="100%"
          bg={formBg}
          p={{ base: 6, md: 8 }}
          borderRadius="2xl"
          boxShadow="lg"
          borderWidth="1px"
          borderColor={formBorder}
          transition="all 0.3s"
          _hover={{
            boxShadow: 'xl',
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={5} align="stretch">
              {/* Organization Section */}
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  mb={3}
                >
                  {t('register.sections.organization')}
                </Text>
                <VStack spacing={4} align="stretch">
                  {isInviteLocked && invite?.organization ? (
                    <Box
                      px={4}
                      py={3}
                      borderRadius="lg"
                      bg="teal.50"
                      border="1px solid"
                      borderColor="teal.200"
                    >
                      <Text fontSize="xs" fontWeight="600" color="teal.700" textTransform="uppercase" letterSpacing="wide" mb={1}>
                        {t('shared.auth.compagnyNameLabel')}
                      </Text>
                      <HStack spacing={2}>
                        <Icon as={LuBuilding2} color="teal.600" />
                        <Text fontSize="sm" color="gray.800" fontWeight="600">{invite.organization.officialName}</Text>
                      </HStack>
                      <Text fontSize="2xs" color="teal.600" mt={1}>
                        {t('auth.invite.lockedHint', { defaultValue: 'Pré-assignée par votre invitation' })}
                      </Text>
                    </Box>
                  ) : (
                    <OrganizationSearch
                      label={t('shared.auth.compagnyNameLabel')}
                      placeholder={t('shared.auth.compagnyNamePlaceholder')}
                      isInvalid={!!errors.companyName}
                      errorMessage={errors.companyName?.message}
                      onOrganizationSelect={(orgId, name) => {
                        // `shouldValidate: true` re-runs the schema check so the red
                        // border + disabled submit clear as soon as the user fills the
                        // field after a failed submit attempt.
                        setValue('organizationId', orgId || undefined, { shouldValidate: true })
                        setValue('companyName', name, { shouldValidate: true })
                      }}
                    />
                  )}
                  <SimpleGrid columns={2} spacing={4}>
                    <ControlInputWithHelp
                      register={{ ...register('firstName') }}
                      label={t('shared.auth.firstNameLabel')}
                      placeholder={t('shared.auth.firstNamePlaceholder')}
                      isInvalid={!!errors.firstName}
                      errorMessage={errors.firstName && errors.firstName.message}
                    />
                    <ControlInputWithHelp
                      register={{ ...register('lastName') }}
                      label={t('shared.auth.lastNameLabel')}
                      placeholder={t('shared.auth.lastNamePlaceholder')}
                      isInvalid={!!errors.lastName}
                      errorMessage={errors.lastName && errors.lastName.message}
                    />
                  </SimpleGrid>
                </VStack>
              </Box>

              <Divider />

              {/* Professional Information Section */}
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  mb={3}
                >
                  {t('register.sections.professional')}
                </Text>
                <VStack spacing={4} align="stretch">
                  <PhoneInput
                    label={t('shared.common.phoneLabel')}
                    placeholder={t('register.phonePlaceholder')}
                    helpText={t('shared.auth.phoneHelp')}
                    isInvalid={!!errors.phoneNumber}
                    errorMessage={errors.phoneNumber && errors.phoneNumber.message}
                    value={watch('phoneNumber')}
                    onChange={(value) => setValue('phoneNumber', value || '', { shouldValidate: true })}
                  />
                  <GenericDropdown
                    value={watch('specialtyId')}
                    onChange={(value) => setValue('specialtyId', value.id, { shouldValidate: true })}
                    errorMessage={errors.specialtyId?.message}
                    useGetHook={useGetSpecialtiesTranslated}
                    label={t('shared.auth.specialtyLabel')}
                    placeholder={t('shared.auth.specialtyPlaceholder')}
                  />
                </VStack>
              </Box>

              <Divider />

              {/* Account Security Section */}
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  mb={3}
                >
                  {t('register.sections.security')}
                </Text>
                <VStack spacing={4} align="stretch">
                  <ControlInputWithHelp
                    register={{ ...register('email') }}
                    label={t('shared.auth.emailLabel')}
                    placeholder={t('shared.auth.emailPlaceholder')}
                    type="email"
                    isInvalid={!!errors.email}
                    errorMessage={errors.email && errors.email.message}
                    validatePattern={emailPattern}
                    showValidation={true}
                    isReadOnly={isInviteLocked}
                  />
                  <ControlPasswordWithValidation
                    register={{ ...register('password') }}
                    label={t('shared.auth.passwordLabel')}
                    placeholder={t('shared.auth.passwordPlaceholder')}
                    isInvalid={!!errors.password}
                    errorMessage={errors.password && errors.password.message}
                    showStrength={true}
                  />
                  <ControlPasswordWithValidation
                    register={{ ...register('confirmPassword') }}
                    label={t('shared.auth.confirmPasswordLabel')}
                    placeholder={t('shared.auth.confirmPasswordPlaceholder')}
                    isInvalid={!!errors.confirmPassword}
                    errorMessage={
                      errors.confirmPassword && errors.confirmPassword.message
                    }
                  />
                </VStack>
              </Box>

              {/* Regulatory consent */}
              <Checkbox
                isChecked={acceptedLegal}
                onChange={(e) => setAcceptedLegal(e.target.checked)}
                colorScheme="purple"
                size="sm"
                alignItems="flex-start"
                spacing={3}
              >
                <Text fontSize="xs" color="gray.600" lineHeight="1.55">
                  {"J'ai lu et j'accepte les "}
                  <Link href="/legal/cgu" target="_blank" color="brand.purple" fontWeight="600">
                    conditions générales d'utilisation
                  </Link>
                  {', les '}
                  <Link href="/legal/cgv" target="_blank" color="brand.purple" fontWeight="600">
                    conditions générales de vente
                  </Link>
                  {' et la '}
                  <Link href="/legal/confidentialite" target="_blank" color="brand.purple" fontWeight="600">
                    politique de confidentialité
                  </Link>
                  {'. Je reconnais que Careflai est un outil d\'aide à la décision qui ne se substitue pas au jugement du professionnel de santé.'}
                </Text>
              </Checkbox>

              {/* Submit Button */}
              <Box pt={2}>
                <ConfirmButton
                  text={t('shared.common.next')}
                  isDisabled={isFormFilled || !acceptedLegal}
                />
              </Box>
            </VStack>
          </form>
        </Box>

        {/* Sign In Link */}
        <VStack spacing={1} w="100%">
          <Link
            href="/login"
            fontSize="sm"
            px={3}
            py={2}
            borderRadius="md"
            transition="all 0.2s"
            _hover={{
              bg: linkHoverBg,
              textDecoration: 'none',
            }}
          >
            <Highlight
              query={t('auth.signIn')}
              styles={{ color: 'brand.purple', fontWeight: 600 }}
            >
              {`${t('auth.alreadyAccount')} ${t('auth.signIn')}`}
            </Highlight>
          </Link>
        </VStack>
      </VStack>
    </Box>
  )
}
